import PropTypes from 'prop-types';
import { MdClose, MdOpenInNew } from 'react-icons/md';
import styles from './SettingsModal.module.css';

const SettingsModal = ({
  isOpen,
  onClose,
  selectedModel,
  setSelectedModel,
  temperature,
  setTemperature,
  topP,
  setTopP,
  maxLength,
  setMaxLength,
  parser,
  setParser,
  availableModels = [],
  modelConfig = {},
  onEditPrompts,
  isDarkMode,
  onToggleDarkMode
}) => {
  if (!isOpen) return null;

  const handleApply = () => {
    onClose();
  };

  const handleRevertToDefault = () => {
    if (availableModels.length > 0) {
      setSelectedModel(availableModels[0]);
    }
    setTemperature(0.0);
    setTopP(0.05);
    // Reset max length based on model default if possible, or a safe default
    setMaxLength(4096);
    setParser('PyMuPDF');
  };

  const isModelsLoading = availableModels.length === 0;
  const currentModelConfig = modelConfig[selectedModel] || {};
  const maxAllowedTokens = currentModelConfig.max_output_tokens || 8192;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Settings</h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close settings"
          >
            <MdClose />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.settingsGroup}>
            <h3 className={styles.groupTitle}>Appearance</h3>
            <div className={styles.settingsGrid}>
              <div style={{ gridColumn: '1 / -1' }}>
                <div className={styles.themeRow}>
                  <div className={styles.themeLabel}>
                    <span>Dark Mode</span>
                  </div>
                  <label className={styles.toggleSwitch}>
                    <input
                      type="checkbox"
                      checked={isDarkMode}
                      onChange={onToggleDarkMode}
                      className={styles.toggleInput}
                    />
                    <span className={styles.toggleSlider}></span>
                  </label>
                </div>
                <span className={styles.hint}>Toggle between light and dark themes</span>
              </div>
            </div>
          </div>

          <div className={styles.settingsGroup}>
            <h3 className={styles.groupTitle}>Model Configuration</h3>
            <div className={styles.settingsGrid}>
              <div className={styles.section} style={{ gridColumn: '1 / -1' }}>
                <label className={styles.label}>Model</label>
                <select
                  value={isModelsLoading ? '' : selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className={styles.select}
                  disabled={isModelsLoading}
                >
                  {isModelsLoading ? (
                    <option value="">Loading models...</option>
                  ) : (
                    availableModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className={styles.section}>
                <label className={styles.label}>Temperature</label>
                <div className={styles.sliderContainer}>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={temperature}
                    onChange={(e) => setTemperature(Number(e.target.value))}
                    className={styles.slider}
                  />
                  <span className={styles.valueDisplay}>{temperature.toFixed(2)}</span>
                </div>
              </div>

              <div className={styles.section}>
                <label className={styles.label}>Top P</label>
                <div className={styles.sliderContainer}>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={topP}
                    onChange={(e) => setTopP(Number(e.target.value))}
                    className={styles.slider}
                  />
                  <span className={styles.valueDisplay}>{topP.toFixed(2)}</span>
                </div>
              </div>

              <div className={styles.section} style={{ gridColumn: '1 / -1' }}>
                <label className={styles.label}>Max Output Length</label>
                <div className={styles.sliderContainer}>
                  <input
                    type="range"
                    min={256}
                    max={maxAllowedTokens}
                    step={256}
                    value={maxLength}
                    onChange={(e) => setMaxLength(Number(e.target.value))}
                    className={styles.slider}
                  />
                  <span className={styles.valueDisplay}>{maxLength}</span>
                </div>
              </div>
            </div>
          </div>


          <div className={styles.settingsGroup}>
            <h3 className={styles.groupTitle}>Prompts & Templates</h3>
            <div className={styles.settingsGrid}>
              <div className={styles.section} style={{ gridColumn: '1 / -1' }}>
                <label className={styles.label}>Custom Prompts</label>
                <button
                  className={styles.secondaryButton}
                  onClick={() => {
                    onClose(); // Close settings when opening prompt editor
                    onEditPrompts();
                  }}
                >
                  Manage Prompt Templates
                  <MdOpenInNew style={{ fontSize: '1.1em' }} />
                </button>
                <span className={styles.hint}>View and edit system prompts used for summarization</span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.revertButton}
            onClick={handleRevertToDefault}
            title="Reset all settings to default values"
          >
            Reset to Default
          </button>
          <button
            className={styles.applyButton}
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

SettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedModel: PropTypes.string.isRequired,
  setSelectedModel: PropTypes.func.isRequired,
  temperature: PropTypes.number.isRequired,
  setTemperature: PropTypes.func.isRequired,
  topP: PropTypes.number.isRequired,
  setTopP: PropTypes.func.isRequired,
  maxLength: PropTypes.number.isRequired,
  setMaxLength: PropTypes.func.isRequired,
  parser: PropTypes.string.isRequired,
  setParser: PropTypes.func.isRequired,
  availableModels: PropTypes.arrayOf(PropTypes.string),
  modelConfig: PropTypes.object,
  onEditPrompts: PropTypes.func.isRequired,
  isDarkMode: PropTypes.bool.isRequired,
  onToggleDarkMode: PropTypes.func.isRequired,
};

export default SettingsModal;
